import { useCallback, useMemo } from "react";
import { formatNumber } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { VictoryPie } from "victory";

import type Transaction from "byzantine/src/Transaction";
import Filters from "byzantine/src/filters";
import {
  categoryTotals,
  UNCATEGORIZED,
  type Direction
} from "../categoryTotals";
import styles from "./CategoryBreakdown.module.scss";

const PALETTE = [
  "#4363d8",
  "#f58231",
  "#3cb44b",
  "#f032e6",
  "#bfef45",
  "#e6194b",
  "#911eb4",
  "#ffe119",
  "#42d4f4",
  "#a9a9a9",
  "#fabed4",
  "#ffd8b1",
  "#fffac8",
  "#aaffc3",
  "#dcbeff",
  "#800000",
  "#9a6324",
  "#808000",
  "#000075",
  "#469990",
  "#000000"
];

const formatCents = (cents: number) => formatNumber(cents / 100, "currency");

const CHART_SIZE = 160;

type CategoryBreakdownProps = {
  transactions: Transaction[];
  direction: Direction;
  heading: string;
  centerLabel: string;
  emptyMessage: string;
};

export function CategoryBreakdown({
  transactions,
  direction,
  heading,
  centerLabel,
  emptyMessage
}: CategoryBreakdownProps) {
  const { l10n } = useLocalization();
  const { slices, total } = useMemo(
    () => categoryTotals(transactions, direction),
    [transactions, direction]
  );

  const labelFor = useCallback(
    (category: string) =>
      category === UNCATEGORIZED
        ? l10n.getString("pfm-category-uncategorized")
        : Filters.capitalizeFirstLetter(category),
    [l10n]
  );

  if (total === 0) {
    return <p className={styles.categoryBreakdownEmpty}>{emptyMessage}</p>;
  }

  return (
    <div className={styles.categoryBreakdown}>
      <div className={styles.categoryBreakdownChart}>
        <VictoryPie
          data={slices.map((slice) => ({
            x: labelFor(slice.category),
            y: slice.total
          }))}
          colorScale={PALETTE}
          innerRadius={65}
          padAngle={1}
          width={CHART_SIZE}
          height={CHART_SIZE}
          padding={0}
          labels={() => null}
        />
        <div className={styles.categoryBreakdownCenter}>
          <span className={styles.categoryBreakdownCenterLabel}>
            {centerLabel}
          </span>
          <span className={styles.categoryBreakdownCenterAmount}>
            {formatCents(total)}
          </span>
        </div>
      </div>

      <div className={styles.categoryBreakdownLegend}>
        <h4 className={styles.categoryBreakdownHeading}>{heading}</h4>
        <ul className={styles.categoryBreakdownList}>
          {slices.map((slice, i) => (
            <li className={styles.categoryBreakdownRow} key={slice.category}>
              <span
                className={styles.categoryBreakdownSwatch}
                style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
              />
              <span className={styles.categoryBreakdownLabel}>
                {labelFor(slice.category)}
              </span>
              <span className={styles.categoryBreakdownAmount}>
                {formatCents(slice.total)}
              </span>
            </li>
          ))}
        </ul>
        <div className={styles.categoryBreakdownTotal}>
          <span>{l10n.getString("pfm-legend-total")}</span>
          <span className={styles.categoryBreakdownAmount}>
            {formatCents(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default CategoryBreakdown;
